import { AdvancedAnalyticsVehicles } from "./fleetApi";

export const isMissingContractData = (
  vehicle: AdvancedAnalyticsVehicles | Record<string, unknown>,
): boolean =>
  vehicle["contractMileage"] == null ||
  vehicle["additionalCostPerKm"] == null ||
  !vehicle["contractStartDate"] ||
  !vehicle["contractEndDate"];

export const getConsumedContractDurationMonth = (
  contractStartDate: null | string,
  contractDurationMonth: number | null,
): number => {
  if (!contractStartDate || !contractDurationMonth) return 0;
  const now = new Date();
  let consumedContractDuration =
    now.getTime() - new Date(contractStartDate).getTime();
  consumedContractDuration =
    consumedContractDuration < 0 ? 0 : consumedContractDuration;

  const consumedContractDurationMonth = Number(
    (consumedContractDuration / (1000 * 60 * 60 * 24 * 30.5)).toFixed(0),
  );

  return consumedContractDurationMonth > contractDurationMonth
    ? contractDurationMonth
    : consumedContractDurationMonth;
};

export const getRemainingContractDurationMonth = (
  contractDurationMonth: number | null,
  consumedContractDurationMonth: number,
) => {
  if (contractDurationMonth === null) return null;
  return contractDurationMonth - consumedContractDurationMonth;
};

export const getContractDurationMonth = (
  contractStartDate: null | string,
  contractEndDate: null | string,
) => {
  if (contractStartDate === null || contractEndDate === null) return null;
  if (contractStartDate >= contractEndDate) return null;

  const contractDuration =
    new Date(contractEndDate).getTime() - new Date(contractStartDate).getTime();
  return Number((contractDuration / (1000 * 60 * 60 * 24 * 30.5)).toFixed(0));
};

export const getMensualMileage = (
  mileage: null | number,
  contractStartDate: null | string,
) => {
  if (mileage === null || contractStartDate === null) return null;

  const now = new Date();
  const duration = now.getTime() - new Date(contractStartDate).getTime();
  const durationMonths = Number(
    (duration / (1000 * 60 * 60 * 24 * 30.5)).toFixed(0),
  );

  return Number((mileage / durationMonths).toFixed(0));
};

export const getProjectionEndOfContract = (
  mensualMileage: null | number,
  contractDurationMonth: null | number,
) => {
  if (mensualMileage === null || contractDurationMonth === null) return null;

  return Number((mensualMileage * contractDurationMonth).toFixed(0));
};

export const getMileageVariationEndContract = (
  contractMileage: number | null | undefined,
  projectionEndOfContract: null | number,
) => {
  if (
    contractMileage === null ||
    contractMileage === undefined ||
    projectionEndOfContract === null
  )
    return null;
  return projectionEndOfContract - contractMileage;
};

export const getAdditionalCostEndContract = (
  mileageVariationEndContract: number | null,
  additionalCostPerKm: number | null,
) => {
  if (mileageVariationEndContract === null || !additionalCostPerKm) return null;

  const result = Number(
    (mileageVariationEndContract * additionalCostPerKm).toFixed(2),
  );
  return result < 0 ? 0 : result;
};

export const getContractMensualMileage = (
  contractMileage: null | number | undefined,
  contractDurationMonth: null | number,
) => {
  if (
    contractMileage === null ||
    contractMileage === undefined ||
    contractDurationMonth === null
  )
    return null;

  return Number((contractMileage / contractDurationMonth).toFixed(0));
};

export const getMileageDeviationPercentage = (
  mensualMileage: number | null,
  mensualContractMileage: null | number,
) => {
  if (mensualMileage === null || mensualContractMileage === null) return null;
  return Number(
    (
      ((mensualMileage - mensualContractMileage) / mensualContractMileage) *
      100
    ).toFixed(2),
  );
};

export const getMonthlyMileageDeviation = (
  mensualMileage: number | null,
  mensualContractMileage: null | number,
) => {
  if (mensualMileage === null || mensualContractMileage === null) return null;
  return mensualMileage - mensualContractMileage;
};

export const fakeAdvancedAnalyticsVehiclesForDemo: (AdvancedAnalyticsVehicles & {
  id: string;
  mensualContractMileage: number;
})[] = [
  {
    uuid: "3ed4b2d0-549d-486d-bd14-61d7bdfcd5e8",
    driverUuid: "3ed4b2d0-549d-486d-bd14-61d7bdfcd5e9",
    reference: "Sur-roulage",
    registrationNumber: "SU-123-RO",
    driverName: "John Doe",
    mensualMileage: 1250,
    mileage: 25000,
    mileageDeviation: "OVERROLLING",
    monthlyMileageKmVariation: 250,
    mileageDeviationPercentage: 25,
    contractMileage: 35000,
    projectionEndOfContract: 36925,
    mileageVariationEndContract: 1925,
    additionalCostEndContract: 269.5,
    additionalCostPerKm: 0.14,
    consumedContractDuration: 39,
    remainingContractDuration: 9,
    contractStartDate: "05/10/2024",
    contractEndDate: "05/10/2026",
    status: "IN_CIRCULATION",
    lastMileageDate: new Date("2024-10-05").toISOString(),
    vehicleType: "PERSONAL",
    model: "CLIO",
    averageConsumption: 5,
    critAir: 1,
    averageEmission: 150,
    energyClass: "D",
    marque: "RENAULT",
    modele: "CLIO",
    energie: "GO",
    createdAt: new Date("2025-12-12").toISOString(),
    vehicleAnalyticCode: null,
    id: "3ed4b2d0-549d-486d-bd14-61d7bdfcd5e8",
    mensualContractMileage: 1000,
  },
  {
    uuid: "9cbed79c-cb5b-47c5-a818-9eeede29cc89",
    driverUuid: "9cbed79c-cb5b-47c5-a818-9eeede29cc80",
    reference: "Correct",
    registrationNumber: "CO-123-RR",
    driverName: "Jane Smith",
    mensualMileage: 1062,
    mileage: 42500,
    mileageDeviation: "CORRECT",
    monthlyMileageKmVariation: 5,
    mileageDeviationPercentage: 0.5,
    contractMileage: 42500,
    projectionEndOfContract: 42500,
    mileageVariationEndContract: 200,
    additionalCostEndContract: 22,
    additionalCostPerKm: 0.11,
    consumedContractDuration: 40,
    remainingContractDuration: 0,
    contractStartDate: "03/01/2022",
    contractEndDate: "07/01/2025",
    status: "IN_CIRCULATION",
    lastMileageDate: new Date("2025-06-01").toISOString(),
    vehicleType: "PERSONAL",
    model: "MEGANE",
    averageConsumption: 5.2,
    critAir: 1,
    averageEmission: 130,
    energyClass: "C",
    marque: "RENAULT",
    modele: "MEGANE",
    energie: "GO",
    createdAt: new Date("2025-12-11").toISOString(),
    vehicleAnalyticCode: null,
    id: "9cbed79c-cb5b-47c5-a818-9eeede29cc89",
    mensualContractMileage: 1057,
  },
  {
    uuid: "abc12345-def6-7890-gh12-ijklmnopqrst",
    driverUuid: "abc12345-def6-7890-gh12-ijklmnopqrsu",
    reference: "Sous-roulage",
    registrationNumber: "SO-800-RL",
    driverName: "Élise Martin",
    mensualMileage: 1600,
    mileage: 80000,
    mileageDeviation: "UNDERROLLING",
    monthlyMileageKmVariation: -400,
    mileageDeviationPercentage: -20,
    contractMileage: 100000,
    projectionEndOfContract: 88000,
    mileageVariationEndContract: -12000,
    additionalCostEndContract: 1680,
    additionalCostPerKm: 0.14,
    consumedContractDuration: 50,
    remainingContractDuration: 10,
    contractStartDate: "01/01/2021",
    contractEndDate: "01/11/2025",
    status: "IN_CIRCULATION",
    lastMileageDate: new Date("2025-06-01").toISOString(),
    vehicleType: "PERSONAL",
    model: "GOLF",
    averageConsumption: 5.3,
    critAir: 1,
    averageEmission: 125,
    energyClass: "C",
    marque: "VOLKSWAGEN",
    modele: "GOLF",
    energie: "GO",
    createdAt: new Date("2025-12-10").toISOString(),
    vehicleAnalyticCode: null,
    id: "abc12345-def6-7890-gh12-ijklmnopqrst",
    mensualContractMileage: 2000,
  },
];
